import { useCallback, useRef, useState } from 'react';

import type { PnlCardData } from '../utils/pnlCardData';

export type ExportAction = 'download' | 'copy';
export type ExportState = 'idle' | 'busy' | 'done' | 'error';

/** How long a finished action keeps showing its confirmation. */
const CONFIRM_MS = 2200;

/** Clipboard image writes need both the API and the ClipboardItem constructor. */
function clipboardSupported(): boolean {
    return (
        typeof ClipboardItem !== 'undefined' &&
        typeof navigator !== 'undefined' &&
        typeof navigator.clipboard?.write === 'function'
    );
}

function fileName(data: PnlCardData): string {
    const roi =
        data.pnlPercent !== null
            ? `${data.pnlValue >= 0 ? 'plus' : 'minus'}${Math.abs(data.pnlPercent).toFixed(0)}pct`
            : 'trade';
    return `snappytrade-${data.coin.toLowerCase()}-${roi}.png`;
}

/**
 * Rasterises the live card node and hands the PNG to one of three actions.
 *
 * The node captured is the same one on screen, at its native 1200×750 — the
 * dialog scales a wrapper around it, never the card, so what you see is what
 * downloads. Captures are serialised: html-to-image walks and clones the
 * subtree, and two overlapping walks interleave badly.
 */
export function usePnlCardExport(
    nodeRef: React.RefObject<HTMLElement | null>,
    data: PnlCardData,
) {
    const [state, setState] = useState<ExportState>('idle');
    const [action, setAction] = useState<ExportAction | null>(null);
    const inFlight = useRef<Promise<Blob> | null>(null);
    const confirmTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const capture = useCallback(async (): Promise<Blob> => {
        // Still returns its promise synchronously, which is what Safari's
        // clipboard requires — the async keyword only wraps the result.
        if (inFlight.current) return inFlight.current;
        const run = (async () => {
            const node = nodeRef.current;
            if (!node) throw new Error('card is not mounted');
            // The clone measures text with whatever faces are loaded; without
            // this the export can fall back to system metrics mid-capture.
            await document.fonts?.ready;
            const { toBlob } = await import('html-to-image');
            const blob = await toBlob(node, {
                pixelRatio: 2,
                backgroundColor: '#000000',
            });
            if (!blob) throw new Error('rasterisation produced no image');
            return blob;
        })();
        inFlight.current = run;
        void run.finally(() => {
            inFlight.current = null;
        });
        return run;
    }, [nodeRef]);

    const settle = useCallback((next: 'done' | 'error') => {
        setState(next);
        if (confirmTimer.current) clearTimeout(confirmTimer.current);
        confirmTimer.current = setTimeout(() => {
            setState('idle');
            setAction(null);
        }, CONFIRM_MS);
    }, []);

    const run = useCallback(
        (which: ExportAction, task: () => Promise<void>) => {
            if (state === 'busy') return;
            setAction(which);
            setState('busy');
            void task().then(
                () => settle('done'),
                () => settle('error'),
            );
        },
        [settle, state],
    );

    const download = useCallback(() => {
        run('download', async () => {
            const blob = await capture();
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName(data);
            link.click();
            URL.revokeObjectURL(url);
        });
    }, [capture, data, run]);

    const copy = useCallback(() => {
        run('copy', async () => {
            // Safari only honours a clipboard write whose data is handed over
            // as a promise created inside the gesture, so pass the capture
            // itself rather than awaiting it first.
            await navigator.clipboard.write([
                new ClipboardItem({ 'image/png': capture() }),
            ]);
        });
    }, [capture, run]);

    return {
        state,
        action,
        download,
        copy,
        canCopy: clipboardSupported(),
    };
}
